// src/encounter/lib/to-lab-order-v3.ts
// The Add lab popup's confirm on the agreed lab model (PROD-2849): the picked order-search item, its
// ticks, the lab and the answers -> the same PlanOrder + lab payload the legacy popup produces, plus the
// PROD-2841 fields (fulfilmentMode, labPriceLineId, memberLabs[]) and the PROD-2640 aoeAnswers[].
//
// What goes where:
//   code             the item's own order coding (Master #325 `code`/`system`; older Master: the clinic code);
//   subTests         a Panel's tests (all fixed) or a clinic panel's ticked tests, not the already-ordered ones;
//   omittedTests     a clinic panel's unticked tests ([] for a Panel: nothing can be left out);
//   alreadyOrderedTests  a clinic panel's tests another lab order of this visit already has;
//   vendor           the chosen lab (orderLabs); none for Patient's choice of lab;
//   panelDefinitionId / panelVersion  a Panel or clinic panel (Patient expands it at sign);
//   memberLabs       every ticked test of a clinic panel, with its own lab or Patient's choice and price line.
// No Bill to (gone in the agreed model) and no cost (Patient looks it up from labPriceLineId at sign).
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { toLabOrder, type LabOrderResult } from './to-lab-order';
import type { LabAoeAnswer, LabMemberLab, LabVendorRef } from './order-payload';
import {
  chipRow,
  clinicDoesNotSendTests,
  isClinicPanelType,
  isPanelType,
  memberCoding,
  memberKey,
  prepText,
  questionKey,
  type FulfilmentMode,
  type LabPick,
} from './lab-order-v3';
import type { LabOrderItemDetail, LabOrderMember, LabOrderQuestion } from '../services/use-lab-order-search';

export interface LabOrderV3Input {
  detail: LabOrderItemDetail;
  pick: LabPick;
  ticked: ReadonlySet<string>;
  already: ReadonlySet<string>;
  questions: readonly LabOrderQuestion[];
  answers: Readonly<Record<string, string>>;
  note?: string;
  orderId: string;
  /** Editing: the saved OrderLabs row ids, kept so a re-save updates that row. */
  savedVendor?: LabVendorRef;
}

/** The item as the coded pick the legacy mapper takes. */
export function itemAsCodeResult(detail: LabOrderItemDetail): CodeSearchResult {
  const code = (detail.code || detail.clinicCode || detail.loinc || '').trim();
  return {
    id: detail.id,
    display: detail.name,
    ...(code ? { code } : {}),
    ...(detail.code?.trim() && detail.system?.trim() ? { system: detail.system.trim() } : {}),
  };
}

const memberMode = (m: LabOrderMember): FulfilmentMode =>
  m.isPatientsChoice || (m.fulfilmentMode ?? '').toLowerCase() === 'patientchoice' ? 'PatientChoice' : 'ContractedLab';

export function toLabOrderV3(input: LabOrderV3Input): LabOrderResult {
  const { detail, pick, ticked, already, questions, answers, note, orderId, savedVendor } = input;
  const clinic = isClinicPanelType(detail.type);
  const panel = isPanelType(detail.type);
  const members = (detail.members ?? []).filter((m) => !m.isReflex);
  const fresh = members.filter((m) => !already.has(memberKey(m)));
  const chosen = clinic ? fresh.filter((m) => ticked.has(memberKey(m))) : panel ? fresh : [];
  const omitted = clinic ? fresh.filter((m) => !ticked.has(memberKey(m))) : [];
  const alreadyMembers = members.filter((m) => already.has(memberKey(m)));

  // The order's own lab. A clinic panel goes to the lab the clinic set (Master's defaultLab), its tests
  // with their own labs in memberLabs.
  const wholePc = clinicDoesNotSendTests(detail);
  const orderLab = wholePc
    ? null
    : clinic
      ? (detail.defaultLab ?? null)
      : pick.kind === 'lab'
        ? pick.lab
        : null;
  const mode: FulfilmentMode = orderLab ? 'ContractedLab' : 'PatientChoice';
  const vendor: LabVendorRef | undefined = orderLab
    ? {
        id: orderLab.labVendorId,
        name: (orderLab.name || orderLab.shortCode || '').trim(),
        ...(savedVendor?.orderLabsId ? { orderLabsId: savedVendor.orderLabsId } : {}),
        ...(savedVendor?.serviceOrderId ? { serviceOrderId: savedVendor.serviceOrderId } : {}),
      }
    : undefined;

  const memberLabs: LabMemberLab[] = clinic
    ? chosen.map((m) => {
        const c = memberCoding(m);
        const pc = wholePc || memberMode(m) === 'PatientChoice';
        return {
          code: c.code,
          ...(c.system ? { system: c.system } : {}),
          fulfilmentMode: pc ? 'PatientChoice' : 'ContractedLab',
          ...(!pc && m.effectiveLabVendorId ? { labVendorId: m.effectiveLabVendorId } : {}),
          ...(!pc && (m.effectiveLabName || m.effectiveLabShortCode) ? { labVendorName: (m.effectiveLabName || m.effectiveLabShortCode) ?? undefined } : {}),
          ...(!pc && m.labPriceLineId ? { labPriceLineId: m.labPriceLineId } : {}),
        };
      })
    : [];

  const aoeAnswers: LabAoeAnswer[] = questions.flatMap((q) => {
    const value = (answers[questionKey(q)] ?? '').trim();
    if (!value) return [];
    return [
      {
        questionText: q.text,
        value,
        ...(q.code?.trim() ? { questionCode: q.code.trim() } : {}),
        ...(q.answerType ? { answerType: q.answerType } : {}),
      },
    ];
  });

  // The plan row's detail line (lib/lab-plan-row.ts): the specimen, and "ordered as one panel".
  const specimen = chipRow(detail, mode === 'PatientChoice')[0]?.replace(/ · 1 draw$/, '');
  const prep = prepText(detail.prepType, detail.prepNote) ?? undefined;
  const patientsChoiceTests = clinic && !wholePc ? chosen.filter((m) => memberMode(m) === 'PatientChoice').map((m) => m.name) : [];

  const base = toLabOrder(
    itemAsCodeResult(detail),
    chosen.map(memberCoding),
    orderId,
    note,
    clinic || panel ? omitted.map(memberCoding) : undefined,
    vendor,
    clinic || panel ? { id: detail.id, version: detail.version } : undefined,
    { specimen: prep && specimen === prep ? undefined : specimen, orderedAsOnePanel: panel },
    clinic ? { alreadyOrderedTests: alreadyMembers.map(memberCoding) } : undefined,
  );
  return {
    planOrder: base.planOrder,
    payload: {
      ...base.payload,
      fulfilmentMode: mode,
      // PROD-2861: the item, stored by Patient so a reload reopens it (and the sign reads its prep).
      labItemId: detail.id,
      ...(orderLab?.labPriceLineId ? { labPriceLineId: orderLab.labPriceLineId } : {}),
      ...(memberLabs.length ? { memberLabs } : {}),
      ...(aoeAnswers.length ? { aoeAnswers } : {}),
      orderSearch: {
        itemId: detail.id,
        type: detail.type,
        ...(prep ? { prep } : {}),
        ...(mode === 'PatientChoice' ? { patientsChoice: true } : {}),
        ...(patientsChoiceTests.length ? { patientsChoiceTests } : {}),
      },
    },
  };
}
